import { type ReactNode } from 'react';

/**
 * EmptyState — Studio Linen v1.0
 *
 * Pattern: icon (optional) → h3 title → body → CTA(s)
 *
 * Tones:
 *   neutral : 그냥 비어 있음 (라이브러리 첫 진입 등)
 *   error   : 실패/오프라인 (brick accent + retry)
 *   success : 완료 (sage accent)
 *
 *   <EmptyState
 *     tone="neutral"
 *     icon={<BookIcon />}
 *     title="아직 책이 없습니다"
 *     description="라이브러리를 둘러보거나 PDF를 업로드해보세요."
 *     primary={<Button>둘러보기</Button>}
 *     secondary={<Button variant="ghost">업로드</Button>}
 *   />
 */
export interface EmptyStateProps {
  tone?: 'neutral' | 'error' | 'success';
  icon?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  primary?: ReactNode;
  secondary?: ReactNode;
  className?: string;
}

export function EmptyState({
  tone = 'neutral', icon, title, description, primary, secondary, className,
}: EmptyStateProps) {
  const cn = ['bq-empty', `bq-empty--${tone}`, className ?? ''].filter(Boolean).join(' ');
  return (
    <div className={cn} role={tone === 'error' ? 'alert' : 'status'} aria-live="polite">
      {icon && <div className="bq-empty__icon" aria-hidden="true">{icon}</div>}
      {title && <h3 className="bq-empty__title">{title}</h3>}
      {description && <p className="bq-empty__description">{description}</p>}
      {(primary || secondary) && (
        <div className="bq-empty__actions">
          {primary}
          {secondary}
        </div>
      )}
    </div>
  );
}
